/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { SmartLink as Link } from '@/utils/smartLink';
import root from 'window-or-global';
import React from 'react';
import { ProductInfoItemContainer } from '../ProductInfoItemContainer';
import styles from './styles.module.scss';
import { ReviewStats } from '@/features/customReviews/components/ReviewStats/ReviewStats';
import { useFeatureFlag } from '@/features/globalConfig';
import { roundValueToPrecision } from '@/components/Rating/utils';
import type { ReviewStatistic } from '../../../customReviews/interface/types';
import { type ProductAnalyticsData } from '../../interface';
import { CUSTOMER_REVIEWS_HASH } from '@/features/customReviews/constants/sectionHash';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';

type ReviewAnalyticsData = {
  rating?: number | null;
  reviewCount?: number | null;
  productAnalyticsData?: ProductAnalyticsData;
};

const trackReviewLinkShelf = ({
  rating,
  reviewCount,
  productAnalyticsData,
}: ReviewAnalyticsData) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data ?? {};

  clickTrack({
    eventType: 'reviewsLinkInteractionShelf',
    customLinkName: `reviewsInteraction - ${pageType}`,
    pageName,
    pageType,
    siteSection,
    productAverageRating: rating,
    productReviewCount: reviewCount,
    ...productAnalyticsData,
  });
};

type Props = {
  skuNumber: number;
  url: string;
  onProductClick?: () => void;
  reviewStatistic?: ReviewStatistic;
  productAnalyticsData?: ProductAnalyticsData;
  reviewsPending?: boolean;
  isMobile?: boolean;
};

export const ProductReviews = ({
  skuNumber,
  reviewStatistic,
  reviewsPending,
  productAnalyticsData,
  url,
  onProductClick,
  isMobile = false,
}: Props) => {
  const locale = useLocale();
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const isUS = locale === countryCodes.us;

  /* analytics Code start : review clickTrack */
  const handleReviewClick = () => {
    const { totalReviewCount, averageOverallRating } = reviewStatistic ?? {};
    // Handle the product click first
    onProductClick?.();

    // Prepare analytics data
    const reviewCount = !reviewStatistic
      ? undefined
      : totalReviewCount
        ? Number(totalReviewCount)
        : null;
    const rating = !reviewStatistic
      ? undefined
      : averageOverallRating
        ? roundValueToPrecision(Number(averageOverallRating), 0.1)
        : null;

    trackReviewLinkShelf({
      rating,
      reviewCount,
      productAnalyticsData,
    });
    /* analytics Code end : review clickTrack */
  };

  const hasReviews =
    reviewStatistic && Number(reviewStatistic.totalReviewCount) > 0 && !reviewsPending;

  if (isCustomReviewsEnabled && (hasReviews || reviewsPending || isMobile)) {
    return (
      <ProductInfoItemContainer
        data-testid="reviews-container"
        className={cx(styles.reviewsContainer, {
          [styles.customReviewsContainer]: isCustomReviewsEnabled,
        })}
      >
        {reviewStatistic && !reviewsPending ? (
          <Link to={`${url}${CUSTOMER_REVIEWS_HASH}`} scroll={false} onClick={handleReviewClick}>
            <ReviewStats reviewStatistic={reviewStatistic} />
          </Link>
        ) : null}
      </ProductInfoItemContainer>
    );
  }

  if (!isCustomReviewsEnabled && isUS) {
    return (
      <ProductInfoItemContainer data-testid="reviews-container" className={styles.reviewsContainer}>
        <Link
          to={`${url}#bv_PDPReviews`}
          scroll={false}
          onClick={handleReviewClick}
          aria-hidden="true"
          tabIndex={-1}
        >
          <div
            data-bv-seo="false"
            data-bv-show="inline_rating"
            data-bv-product-id={skuNumber}
            className={`${styles.reviews}`}
            id={`inlineRatingsBV-${skuNumber}`}
          />
        </Link>
      </ProductInfoItemContainer>
    );
  }

  return null;
};
